import {makeAutoObservable} from 'mobx'
import {
    CustomerDTO,
    DepartmentDTO,
    MachineDTO,
    PlantDTO,
    PositionDTO,
    RegionDTO
} from '../Models/ApiModels'
import {LocalStorageGet, LocalStorageSet} from '../Services/Helpers'

export type FilterKeyValue = { [key: string]: string }

export const FILTER_SATE_KEY = 'filterState'

class FiltersStore {
    public filters: FilterKeyValue = {}
    public resetVisible: boolean = false
    private _filterChangeHandler: Function | null = null

    constructor() {
        makeAutoObservable(this)
        this.filters = this.defaultFilter
        this.restoreState()
    }

    public get defaultFilter() {
        return {
            region: '',
            customer: '',
            plant: '',
            department: '',
            machine: '',
            position: '',
            machineType: '',
            recipe: '',
            revision: ''
        }
    }

    public showResetFilters(changeHandler: Function) {
        this.resetVisible = true
        this._filterChangeHandler = changeHandler
    }

    public hideResetFilters() {
        this.resetVisible = false
        this._filterChangeHandler = null
    }

    public onResetClick = () => {
        const newFilters = this.defaultFilter
        newFilters.customer = this.filters.customer || ''
        this.onFilterChange(newFilters)
        this._filterChangeHandler && this._filterChangeHandler()
    }

    public setCustomerFilter(newCustomer: string) {
        const newFilters = this.defaultFilter
        newFilters.customer = newCustomer || ''
        this.onFilterChange(newFilters)
    }

    public onCustomerChange = (changeHandler: (key: keyof FilterKeyValue) => void) => (value?: CustomerDTO) => {
        this.setCustomerFilter(value?.id || '')
        changeHandler('customer')
    }

    public onRegionChange = (changeHandler: (key: keyof FilterKeyValue) => void) => (value?: RegionDTO) => {
        const newFilters = this.defaultFilter
        newFilters.customer = value?.companyId || this.filters.customer
        newFilters.region = value?.id || ''
        this.onFilterChange(newFilters)
        changeHandler('region')
    }

    public onPlantChange = (changeHandler: (key: keyof FilterKeyValue) => void) => (value?: PlantDTO) => {
        const newFilters = this.defaultFilter
        if (value) {
            newFilters.customer = value.companyId
            newFilters.region = value.regionId || ''
            newFilters.plant = value.id
        } else {
            newFilters.customer = this.filters.customer
            newFilters.region = this.filters.region
        }
        this.onFilterChange(newFilters)
        changeHandler('plant')
    }

    public onDepartmentChange = (changeHandler: (key: keyof FilterKeyValue) => void) => (selected?: DepartmentDTO[]) => {
        const newFilters = {...this.filters}
        newFilters.department = selected?.map(d => d.id).join(',') || ''
        newFilters.machine = ''
        newFilters.position = ''
        this.onFilterChange(newFilters)
        changeHandler('department')
    }

    public onMachineChange = (changeHandler: (key: keyof FilterKeyValue) => void) => (selected?: MachineDTO[]) => {
        const newFilters = {...this.filters}
        newFilters.machine = selected?.map(d => d.id).join(',') || ''
        if (selected?.length && !newFilters.department) {
            newFilters.department = selected[0].departmentId!
        }
        newFilters.position = ''
        this.onFilterChange(newFilters)
        changeHandler('machine')
    }

    public onPositionChange = (changeHandler: (key: keyof FilterKeyValue) => void) => (selected?: PositionDTO[]) => {
        const newFilters = {...this.filters}
        newFilters.position = selected?.map(d => d.id).join(',') || ''
        this.onFilterChange(newFilters)
        changeHandler('position')
    }

    public setMachineTypeFilter(newMachineType: string) {
        const newFilters = {...this.filters}
        newFilters.machineType = newMachineType || ''
        newFilters.recipe = ''
        newFilters.revision = ''
        this.onFilterChange(newFilters)
    }

    public setRecipeFilter(newRecipe: string) {
        const newFilters = {...this.filters}
        newFilters.recipe = newRecipe
        newFilters.revision = ''
        this.onFilterChange(newFilters)
    }

    public restoreState() {
        const hasValue = LocalStorageGet<FilterKeyValue>(FILTER_SATE_KEY)
        if (hasValue) {
            this.filters = hasValue
        }
    }

    public storeValue() {
        LocalStorageSet(FILTER_SATE_KEY, this.filters)
    }

    public onFilterChange(newFilters: FilterKeyValue) {
        this.filters = {
            ...this.filters, ...newFilters,
        }
        this.storeValue()
    }

}

export const filtersStore = new FiltersStore()